// src/utils/devToolsProtection.js
// ✅ COMPLETELY REWRITTEN - Super Admin & Admin get full DevTools access

let protectionActive = false;
let devToolsOpen = false;
let toastTimeout = null;
let consoleClearInterval = null;

// ── Toast notification ────────────────────────────────────────────────────────
const showToast = (message, icon = '🔒') => {
  // Avoid spamming — only one toast at a time
  const existing = document.getElementById('security-toast');
  if (existing) return;

  const toast = document.createElement('div');
  toast.id = 'security-toast';
  toast.style.cssText = `
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%) translateY(80px);
    background: rgba(15, 23, 42, 0.96);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #f1f5f9;
    padding: 12px 20px;
    border-radius: 10px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 13px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 999999;
    box-shadow: 0 8px 32px rgba(0,0,0,0.4);
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease;
    opacity: 0;
    white-space: nowrap;
    pointer-events: none;
  `;

  toast.innerHTML = `
    <span style="font-size:15px">${icon}</span>
    <span>${message}</span>
  `;

  document.body.appendChild(toast);

  // Slide in
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      toast.style.transform = 'translateX(-50%) translateY(0)';
      toast.style.opacity = '1';
    });
  });

  // Slide out and remove
  clearTimeout(toastTimeout);
  toastTimeout = setTimeout(() => {
    toast.style.transform = 'translateX(-50%) translateY(80px)';
    toast.style.opacity = '0';
    setTimeout(() => toast?.remove(), 300);
  }, 3000);
};

// ── DevTools size detection ───────────────────────────────────────────────────
const isDevToolsOpen = () => {
  const threshold = 150;
  const widthDiff  = window.outerWidth  - window.innerWidth;
  const heightDiff = window.outerHeight - window.innerHeight;
  return widthDiff > threshold || heightDiff > threshold;
};

// ── Handle detected DevTools ──────────────────────────────────────────────────
const handleDevToolsOpen = () => {
  if (devToolsOpen) return; // already handled
  devToolsOpen = true;
  showToast('DevTools disabled for security reasons', '🔒');
  // Blur the page subtly so data isn't easy to read
  const root = document.getElementById('root');
  if (root) root.style.filter = 'blur(4px)';
};

const handleDevToolsClosed = () => {
  if (!devToolsOpen) return;
  devToolsOpen = false;
  // Remove blur
  const root = document.getElementById('root');
  if (root) root.style.filter = '';
};

// ── Keyboard shortcut blocking ────────────────────────────────────────────────
const handleKeyDown = (e) => {
  if (!protectionActive) return;

  const blocked =
    e.key === 'F12' ||
    ((e.ctrlKey || e.metaKey) && e.shiftKey && ['I', 'J', 'C', 'K'].includes(e.key)) ||
    ((e.ctrlKey || e.metaKey) && e.key === 'u');

  if (blocked) {
    e.preventDefault();
    e.stopPropagation();
    showToast('DevTools disabled for security reasons', '🔒');
    return false;
  }
};

// ── Right-click blocking ──────────────────────────────────────────────────────
const handleContextMenu = (e) => {
  if (!protectionActive) return;
  e.preventDefault();
  showToast('Right-click disabled for security reasons', '🛡️');
  return false;
};

// ── Continuous monitoring ─────────────────────────────────────────────────────
let monitorInterval = null;

const startMonitoring = () => {
  monitorInterval = setInterval(() => {
    if (!protectionActive) return;
    if (isDevToolsOpen()) {
      handleDevToolsOpen();
    } else {
      handleDevToolsClosed();
    }
  }, 1500);
};

const stopMonitoring = () => {
  if (monitorInterval) {
    clearInterval(monitorInterval);
    monitorInterval = null;
  }
};

// ── Helper function to check if user is admin/super_admin ────────────────────
const isAdminUser = (user) => {
  if (!user || !user.role) return false;
  
  const role = String(user.role).toLowerCase().trim();
  
  // Check for admin OR super_admin (with underscore or space)
  return (
    role === 'admin' ||
    role === 'super_admin' ||
    role === 'super admin' ||
    role === 'superadmin'
  );
};

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Call this after login, passing the user object.
 * Protection ONLY activates for supervisor and technician roles.
 * Admin and Super Admin have FULL DevTools access.
 *
 * Usage in App.js:
 *   import { initDevToolsProtection } from './utils/devToolsProtection';
 *   initDevToolsProtection(user); // call after login
 */
export const initDevToolsProtection = (user) => {
  // ✅ CRITICAL: Check if user is admin/super_admin
  if (isAdminUser(user)) {
    console.clear();
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color:#6366f1');
    console.log('%c🔓 ADMIN ACCESS GRANTED', 'color:#22c55e;font-size:18px;font-weight:700;');
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color:#6366f1');
    console.log('%cDevTools Protection: DISABLED', 'color:#86efac;font-size:14px;font-weight:600;');
    console.log('%cRole: ' + user.role, 'color:#86efac;font-size:14px;');
    console.log('%cConsole Access: FULL', 'color:#86efac;font-size:14px;');
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color:#6366f1');
    console.log('');
    console.log('%cWelcome, ' + (user.name || 'Admin') + '!', 'color:#a5b4fc;font-size:13px;');
    console.log('%cYou have unrestricted console access.', 'color:#94a3b8;font-size:12px;');
    console.log('');
    
    // Make absolutely sure protection is OFF
    protectionActive = false;
    
    // Clear any existing intervals
    if (monitorInterval) {
      clearInterval(monitorInterval);
      monitorInterval = null;
    }
    if (consoleClearInterval) {
      clearInterval(consoleClearInterval);
      consoleClearInterval = null;
    }
    
    // Remove any existing listeners
    document.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('contextmenu', handleContextMenu, true);
    
    // Exit completely - NO PROTECTION
    return;
  }

  // ✅ For non-admin users (supervisor, technician), activate protection
  console.log('[Security] Non-admin user detected - activating DevTools protection');
  console.log('[Security] Role:', user?.role || 'unknown');
  
  protectionActive = true;

  // Add event listeners
  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('contextmenu', handleContextMenu, true);
  
  // Start monitoring
  startMonitoring();

  // Clear console periodically for non-admin users
  consoleClearInterval = setInterval(() => {
    if (protectionActive) {
      console.clear();
      console.log('%c🔒 This application is protected.', 'color:#6366f1;font-size:16px;font-weight:700;');
      console.log('%cUnauthorized access attempts are monitored.', 'color:#94a3b8;font-size:13px;');
    }
  }, 5000);

  // Initial console clear
  console.clear();
  console.log('%c🔒 This application is protected.', 'color:#6366f1;font-size:16px;font-weight:700;');
  console.log('%cUnauthorized access attempts are monitored.', 'color:#94a3b8;font-size:13px;');
};

/**
 * Call this on logout to clean up all listeners.
 */
export const destroyDevToolsProtection = () => {
  console.log('[Security] Destroying DevTools protection');
  
  protectionActive = false;
  devToolsOpen = false;
  
  // Remove event listeners
  document.removeEventListener('keydown', handleKeyDown, true);
  document.removeEventListener('contextmenu', handleContextMenu, true);
  
  // Stop monitoring
  stopMonitoring();
  
  // Clear console interval
  if (consoleClearInterval) {
    clearInterval(consoleClearInterval);
    consoleClearInterval = null;
  }

  // Remove blur if still applied
  const root = document.getElementById('root');
  if (root) root.style.filter = '';
  
  console.log('[Security] DevTools protection destroyed');
};

export default initDevToolsProtection;
